import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { DEMO_JWT, DEMO_ORG_ID, DEMO_USER_ID } from '@/lib/demoMode';
import {
  demoDisplayName,
  getDemoSessionSnapshot,
  subscribeDemoSession,
} from '@/lib/demoSession';
import * as chatPrefs from '@/utils/chatPrefs';
import { useStore } from '@/stores/useStore';
import { DEFAULT_CHAT_APPROVAL_MODE } from '@/stores/chatApprovalMode';
import type { AuthContextValue, AuthProfile, AuthUser } from './useAuth';

/**
 * Demo-mode stand-in for useAuth. Selected at module scope in useAuth.ts —
 * never per-render, so rules-of-hooks hold. No Stytch SDK involvement.
 *
 * Not a frozen literal: the name is editable on `/account` and the org role
 * moves when the demo transfers ownership, so both come from the demo session
 * store. Everything else here is genuinely constant.
 */
const DEMO_EMAIL = 'demo@khorium.ai';

export function useDemoAuth(): AuthContextValue {
  const session = useSyncExternalStore(
    subscribeDemoSession,
    getDemoSessionSnapshot,
  );

  const profile = useMemo<AuthProfile>(
    () => ({
      email: DEMO_EMAIL,
      name: demoDisplayName(session),
      firstName: session.firstName,
      lastName: session.lastName,
    }),
    [session],
  );

  const user = useMemo<AuthUser>(
    () => ({
      id: DEMO_USER_ID,
      // A team the demo visitor owns, so `/account` shows the real team
      // management surface rather than the "not linked to an organization"
      // empty state. `TeamManagement` branches on this BEFORE any query runs,
      // so mocking `/api/organization` alone would change nothing on screen.
      // The org and member records themselves come from the MSW handler; this
      // is only the caller's own membership, which is what the real
      // `/auth/current` carries.
      organization: {
        id: DEMO_ORG_ID,
        role: session.orgRole,
      },
      ...profile,
    }),
    [profile, session.orgRole],
  );

  // Like the real authFetch, the body goes out exactly as the caller built it.
  const authFetch = useCallback(
    (url: string, options: RequestInit = {}) =>
      fetch(url, {
        ...options,
        headers: {
          ...(options.headers as Record<string, string>),
          Authorization: `Bearer ${DEMO_JWT}`,
        },
        cache: 'no-store',
      }),
    [],
  );

  const logout = useCallback(async () => {
    // No control calls this while `canEndSession` is false, but `main.tsx`
    // registers it as the session-expiry handler like any other, so a 401 from
    // a future mock would reach it. Kept working for that path: wipe chat state
    // and return to `/`, which is the closest the demo has to ending a session.
    useStore.getState().setChatApprovalMode(DEFAULT_CHAT_APPROVAL_MODE);
    chatPrefs.clearOnLogout();
    window.location.href = '/';
  }, []);

  return {
    user,
    profile,
    isLoading: false,
    isAuthenticated: true,
    // Demo identity is a literal — there is no request to be pending on.
    isIdentityPending: false,
    isEmailVerified: true,
    // There is a profile to open: `/account` is a real page in the demo.
    canManageSession: true,
    // But no session to end — `logout` above returns you as this same static
    // user, so consumers render the control disabled rather than ignoring it.
    canEndSession: false,
    // And no other workspace to move to: the demo is one static organization.
    canSwitchWorkspace: false,
    listWorkspaces: async () => {
      throw new Error('Demo workspace switching is unavailable');
    },
    switchWorkspace: async () => {
      throw new Error('Demo workspace switching is unavailable');
    },
    error: null,
    retry: () => undefined,
    logout,
    authFetch,
  };
}
